/* eslint-disable no-param-reassign */
/**
 * @fileoverview Hook de Actions Layer para gestión de selección de productos en el Inbox.
 * Permite seleccionar, limpiar y manejar productos asociados a la conversación, integrando lógica de negocio y optimización de performance.
 *
 * @module useProductSelectionActions
 * @category Hook - Actions Layer
 * @since 3.0.0
 * @version 3.1.0
 *
 * @typedef {Object} UseProductSelectionActionsParams
 * @property {Object} conversation - Objeto de conversación activo
 * @property {React.RefObject} textareaRef - Ref del textarea del chat
 * @property {Object|null} selectedProduct - Producto seleccionado
 * @property {function} setSelectedProduct - Setter del producto seleccionado
 * @property {Object|null} [replyMessage] - Mensaje al que se responde (opcional)
 * @property {function} [clearReplyMessage] - Limpia el estado de respuesta (opcional)
 *
 * @function useProductSelectionActions
 * @param {UseProductSelectionActionsParams} params - Parámetros del hook
 * @returns {Object} Métodos de selección y envío de producto
 * @property {function} handleProductSelection - Aplica selección desde ProductSelection
 * @property {function} formatProductForChat - Formatea producto para vista previa
 * @property {function} handleSendProductFromInput - Envía producto desde el input (caption)
 * @property {function} clearSelectedProduct - Quita producto seleccionado
 * @property {function} removeProductImage - Quita una imagen del producto seleccionado
 *
 * @description
 * **Características:**
 * - Gestión de selección y limpieza de productos
 * - Integración con lógica de negocio de la conversación
 * - Memoización para performance
 * - Uso exclusivo en capa Actions
 *
 * **Atomic Design:**
 * - Nivel: Hook - Actions Layer
 *
 * @example
 * const {
 *   handleSendProductFromInput,
 *   clearSelectedProduct,
 *   selectedProduct
 * } = useProductSelectionActions({
 *   conversation,
 *   textareaRef,
 *   selectedProduct,
 *   setSelectedProduct,
 *   replyMessage,
 *   clearReplyMessage
 * });
 *
 * @see {@link ProductSelectorMC} Componente de selección de producto
 * @see {@link ChatActionsBarMC} Barra de acciones del chat
 */
/**
 * @fileoverview Hook Actions Layer para manejo de selección de productos desde ProductSelection.
 * @module useProductSelectionActions
 * @category Hook - Actions Layer
 * @since 1.0.0
 * @version 1.0.0
 */
import { useEffect, useCallback, useState } from 'react';
import { cloneDeep, forEach, isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { sharingInformationService } from '../../../../services/sharingInformationService';
import { formatProductMessage } from '../../../../util/messageUtil';
import useCompleteMessageActions from './useCompleteMessageActions';

function useProductSelectionActions({
  conversation,
  textareaRef,
  selectedProduct,
  setSelectedProduct,
  replyMessage,
  clearReplyMessage
}) {
  const [isCollection, setIsCollection] = useState(false);
  const retailerInfo = useSelector(
    (state) => state.retailerUsersReducer.retailer_info
  );
  const { sendAndAddMessage, sendAndAddMessageWithFiles } = useCompleteMessageActions();

  /**
   * Formatea un producto para el chat.
   */
  const formatProductForChat = useCallback((product, sendCollection) => {
    const newProduct = cloneDeep(product);

    if (sendCollection) {
      // Mostrar todas las imágenes para colección
      forEach(newProduct.image_urls, (image) => {
        image.display = true;
      });
    } else {
      // Mostrar solo la primera imagen para producto individual
      forEach(newProduct.image_urls, (image, index) => {
        image.display = index === 0;
      });
    }

    return newProduct;
  }, []);

  /**
   * Maneja la selección de producto desde ProductSelection.
   */
  const handleProductSelection = useCallback((data) => {
    if (!data.selectProduct || !data.selectedProduct) return;

    const formattedProduct = formatProductForChat(data.selectedProduct, data.sendCollection);

    // Formatear mensaje del producto
    const productString = formatProductMessage(
      formattedProduct,
      retailerInfo?.tax_amount || 0,
      conversation?.platform || 'whatsapp'
    );

    // Actualizar valor en react-hook-form
    // Establecer el mensaje en el textarea directamente
    if (textareaRef.current) {
      textareaRef.current.value = productString;
      textareaRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Optimizar manipulación del DOM con requestAnimationFrame
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.focus();
        const textLength = productString.length;
        textarea.setSelectionRange(textLength, textLength);

        // Trigger de evento para actualizar altura si es necesario
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });

    setSelectedProduct(formattedProduct);
    setIsCollection(data.sendCollection);
  }, [
    formatProductForChat,
    textareaRef,
    retailerInfo,
    conversation,
    setSelectedProduct,
    setIsCollection
  ]);

  const clearSelectedProduct = useCallback(() => {
    setSelectedProduct(null);
    setIsCollection(false);

    // Si hay textarea, limpiar y actualizar estado
    if (textareaRef.current) {
      textareaRef.current.value = '';
      textareaRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }, [setSelectedProduct, textareaRef]);

  /**
   * Elimina una imagen específica del producto seleccionado
   */
  const removeProductImage = useCallback((imageIndex) => {
    setSelectedProduct((currentProduct) => {
      if (!currentProduct || !currentProduct.image_urls) {
        return currentProduct;
      }

      const updatedProduct = cloneDeep(currentProduct);

      // Eliminar la imagen del array
      updatedProduct.image_urls = updatedProduct.image_urls.filter((_, index) => index !== imageIndex);

      return updatedProduct;
    });
  }, [setSelectedProduct]);

  const handleSendProductFromInput = useCallback((caption) => {
    if (!selectedProduct) return false;

    const copyedProduct = cloneDeep(selectedProduct);
    const rawImagesToSend = isCollection
      ? copyedProduct.image_urls
      : [copyedProduct.image_urls && copyedProduct.image_urls[0]];

    const resolveImageUrl = (image) => image?.jpgUrl || image?.url;

    // Aseguramos que sea un array con objetos válidos (jpgUrl preferido, igual que WsChatMessages)
    const imagesToSend = (rawImagesToSend || []).filter(
      (image) => image && resolveImageUrl(image)
    );

    const replyToId = replyMessage?.id;
    const replyPayload = replyToId != null && replyToId !== '' ? { reply_to: replyToId } : {};

    if (isEmpty(imagesToSend)) {
      sendAndAddMessage({
        type: 'text',
        message: caption,
        platform: conversation?.platform,
        customer_id: conversation?.id,
        isComment: conversation?.isComment || false,
        ...replyPayload
      });
    } else {
      imagesToSend.forEach((image, index) => {
        const imageUrl = resolveImageUrl(image);
        if (!image || !imageUrl) return;

        const messageData = {
          type: 'image',
          url: imageUrl,
          caption: index === 0 ? caption : '', // Solo la primera imagen lleva el caption
          platform: conversation?.platform,
          customer_id: conversation?.id,
          isComment: conversation?.isComment || false,
          ...(index === 0 ? replyPayload : {})
        };

        sendAndAddMessageWithFiles(messageData);
      });
    }

    clearSelectedProduct();
    clearReplyMessage?.();
    return true;
  }, [
    selectedProduct,
    isCollection,
    conversation,
    sendAndAddMessageWithFiles,
    clearSelectedProduct,
    sendAndAddMessage,
    replyMessage,
    clearReplyMessage
  ]);

  /**
   * Efecto para suscribirse a eventos de selección de productos.
   */
  useEffect(() => {
    const subscription$ = sharingInformationService.getSubject();

    const observable = subscription$.subscribe((data) => {
      if (data.selectProduct) {
        handleProductSelection(data);
      }
    });

    return () => {
      observable.unsubscribe();
    };
  }, [handleProductSelection]);

  return {
    handleProductSelection,
    formatProductForChat,
    handleSendProductFromInput,
    clearSelectedProduct,
    removeProductImage
  };
}

export default useProductSelectionActions;
